/**
 * AmbassadorSubmitProperty — Add a property to the ambassador's pipeline
 *
 * Simplified form: property details + contact info only.
 * Calls ambassador-lead-manage edge function with action: 'create'.
 * Creates property in 'new' pipeline status.
 *
 * URL: /ambassador/submit-property
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { POS_GOLD } from '../../config/posRoomStatus';


const PROPERTY_TYPES = [
  'Hotel', 'Boutique Hotel', 'Guest House', 'B&B', 'Lodge', 'Resort', 'Aparthotel', 'Other',
];

function AmbassadorSubmitProperty() {
  const { supabase } = useAuth();

  const [propertyName, setPropertyName] = useState('');
  const [propertyType, setPropertyType] = useState('Hotel');
  const [city, setCity] = useState('');
  const [province, setProvince] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [notes, setNotes] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!propertyName.trim()) { setError('Property name is required'); return; }
    // Contact name is optional — ambassador may not have it yet

    setSubmitting(true);
    try {
      const { data, error: fnError } = await supabase.functions.invoke('ambassador-lead-manage', {
        body: {
          action: 'create',
          property_name: propertyName.trim(),
          property_type: propertyType,
          city: city.trim() || undefined,
          province: province.trim() || undefined,
          contact_name: contactName.trim(),
          contact_email: contactEmail.trim() || undefined,
          contact_phone: contactPhone.trim() || undefined,
          notes: notes.trim() || undefined,
        },
      });

      if (fnError) throw new Error(fnError.message || 'Failed to submit property');
      if (!data?.success) throw new Error(data?.error || 'Failed to submit property');

      setSuccess(true);
    } catch (err: unknown) {
      console.error('Submit property error:', err);
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="page-content">
        <div style={{
          maxWidth: '480px', margin: '40px auto', textAlign: 'center',
          padding: '40px 24px', background: 'var(--color-white)', borderRadius: 'var(--radius-lg)', border: '1px solid #e5e7eb',
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>&#10003;</div>
          <h2 style={{ margin: '0 0 8px', fontSize: 'var(--text-3xl)' }}>Property Added</h2>
          <p style={{ color: 'var(--color-text-light)', fontSize: 'var(--text-base)', margin: '0 0 24px' }}>
            Your property has been added to your pipeline. You can track it and add notes from My Properties.
          </p>
          <div className="flex justify-center gap-md">
            <button
              className="btn btn-sm"
              onClick={() => {
                setSuccess(false);
                setPropertyName(''); setCity(''); setProvince('');
                setContactName(''); setContactEmail(''); setContactPhone('');
                setNotes(''); setPropertyType('Hotel');
              }}
              style={{ background: 'var(--color-bg)', color: 'var(--color-sidebar)', border: '1px solid #d1d5db' }}
            >
              Submit Another
            </button>
            <Link to="/ambassador/properties" className="btn btn-sm" style={{ background: 'var(--color-bg)', color: 'var(--color-sidebar)', border: '1px solid #d1d5db', textDecoration: 'none' }}>
              My Properties
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div style={{ maxWidth: '560px', margin: '0 auto' }}>
        <form onSubmit={handleSubmit}>
          {/* Property Details */}
          <div className="card mb-lg">
            <div className="card-header"><h3 className="card-title">Property Details</h3></div>
            <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label className="form-label">Property Name *</label>
                <input type="text" className="form-input" value={propertyName} onChange={e => setPropertyName(e.target.value)} placeholder="e.g. The Grand Hotel" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label className="form-label">Property Type</label>
                  <select className="form-input" value={propertyType} onChange={e => setPropertyType(e.target.value)}>
                    {PROPERTY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="form-label">City</label>
                  <input type="text" className="form-input" value={city} onChange={e => setCity(e.target.value)} placeholder="e.g. Cape Town" />
                </div>
              </div>
              <div>
                <label className="form-label">Province</label>
                <input type="text" className="form-input" value={province} onChange={e => setProvince(e.target.value)} placeholder="e.g. Western Cape" />
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="card mb-lg">
            <div className="card-header"><h3 className="card-title">Contact Person</h3></div>
            <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label className="form-label">Name</label>
                <input type="text" className="form-input" value={contactName} onChange={e => setContactName(e.target.value)} placeholder="e.g. John Smith" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label className="form-label">Email</label>
                  <input type="email" className="form-input" value={contactEmail} onChange={e => setContactEmail(e.target.value)} placeholder="gm@hotel.com" />
                </div>
                <div>
                  <label className="form-label">Phone</label>
                  <input type="tel" className="form-input" value={contactPhone} onChange={e => setContactPhone(e.target.value)} placeholder="+27..." />
                </div>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="card mb-lg">
            <div className="card-header"><h3 className="card-title">Notes (optional)</h3></div>
            <div style={{ padding: '16px' }}>
              <textarea className="form-input" rows={3} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any additional context about this property..." />
            </div>
          </div>

          {error && (
            <div style={{ padding: '12px 16px', background: 'var(--color-error-bg)', border: '1px solid #fecaca', borderRadius: 'var(--radius)', color: 'var(--color-error)', fontSize: 'var(--text-base)', marginBottom: '16px' }}>
              {error}
            </div>
          )}

          <button type="submit" disabled={submitting} style={{
            width: '100%', padding: '14px', background: submitting ? 'var(--color-text-light)' : POS_GOLD, color: 'var(--color-sidebar)',
            border: 'none', borderRadius: 'var(--radius)', fontSize: 'var(--text-lg)', fontWeight: 700, cursor: submitting ? 'not-allowed' : 'pointer',
          }}>
            {submitting ? 'Adding...' : 'Add Property'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default AmbassadorSubmitProperty;
